import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import Logo from './Logo';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <motion.header
      data-testid="main-header"
      className={`sticky top-0 z-50 backdrop-blur-xl transition-all duration-300 ${
        isScrolled ? 'bg-[#F7FAFC]/95 border-b border-[#5A6B7E]/10 shadow-sm' : 'bg-[#F7FAFC]/90'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center">
            <Logo />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`text-sm font-medium transition-colors duration-300 relative group ${
                  location.pathname === link.path
                    ? 'text-[#0A4DA6]'
                    : 'text-[#5A6B7E] hover:text-[#0A4DA6]'
                }`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-[#3FC9E8] transition-all duration-300 group-hover:w-full ${
                    location.pathname === link.path ? 'w-full' : ''
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <Link
            to="/contact"
            data-testid="header-cta-button"
            className="hidden md:block bg-[#3FC9E8] text-[#0F2A4A] font-medium px-6 py-3 hover:bg-[#1A9FE0] hover:text-white transition-all duration-300 active:scale-95"
          >
            Book a FREE Strategy Call
          </Link>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-[#0A4DA6] p-2"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden pb-6 space-y-4"
          >
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block text-base font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-[#0A4DA6]'
                    : 'text-[#5A6B7E]'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-cta-button"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block bg-[#3FC9E8] text-[#0F2A4A] font-medium px-6 py-3 text-center hover:bg-[#1A9FE0] hover:text-white transition-all duration-300"
            >
              Book a FREE Strategy Call
            </Link>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

export default Header;